import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { Cart } from "components/icons/Cart";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { Product } from "pages/Billing/Product/Product";
import { useState } from "react";
import { useRouteMatch } from "react-router-dom";
import InkleCreditCoin from "static/images/InkleCreditCoin.svg";
import { useGetGroupCouponQuery } from "store/apis/billing";
import { useGetCartQuery } from "store/apis/products";
import { useGetCreditsQuery } from "store/apis/refrral";
import { pluralize } from "utils/pluralize";
import ApplyCouponModal from "components/billing/ApplyCouponModal";
import { DashboardLayout } from "components/DashboardLayout";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import ConditionalToolTip from "components/design/conditionalToolTip";
import WarningCircle from "static/images/WarningCircle.svg";
import { formatNumber } from "utils/formatNumber";
import { OPEN_CHECKOUT } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";

export const Services = () => {
  usePageTitle("Products & Services");
  const { isUserTypeForeignCA, isForeignCA, isPersonalTaxUser } =
    useRoleBasedView();
  const { url } = useRouteMatch();
  const isFCA = isUserTypeForeignCA || isForeignCA;
  const { trackEvent } = useAnalytics();
  const { isMobile } = useWindowSize();

  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data: cart, isFetching: isCartFetching } = useGetCartQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId || !entityId }
  );
  const { data: couponData } = useGetGroupCouponQuery(
    { groupId },
    { skip: !groupId }
  );

  const { data, isLoading: isCreditsLoading } = useGetCreditsQuery(
    { groupId },
    { skip: !groupId }
  );
  const { total_credits } = data || {};
  const isLessCredits = total_credits?.toString() && total_credits < 100;
  const currentEntity = entities.find(({ uuid }) => entityId === uuid);
  const [openApplyCoupon, setOpenApplyCoupon] = useState(false);

  const LinkComponent = isCartFetching ? "div" : ConditionalLink;
  const { link } = useConstructInternalLink();

  const handleOpenCart = () => {
    trackEvent(OPEN_CHECKOUT, { type: "services" });
  };

  return (
    <>
      <DashboardLayout
        header={
          <Header
            className="t-py-1"
            v2
            title="Products & Services"
            right={
              <div className="t-flex t-items-center t-gap-1.5">
                {!isFCA && (
                  <div className="t-flex t-items-center t-gap-2 t-shrink-0">
                    {Boolean(couponData?.coupon_code) ? (
                      <ToolTip text={couponData?.coupon_code}>
                        <span className="t-text-green-80 t-text-body-sm t-cursor-default t-py-2 t-px-3 t-whitespace-nowrap">
                          Coupon Applied
                        </span>
                      </ToolTip>
                    ) : (
                      <span
                        onClick={() => setOpenApplyCoupon(true)}
                        className="t-text-text-60 t-text-body-sm t-cursor-pointer hover:t-text-text-100 t-py-2 t-px-3 t-whitespace-nowrap"
                      >
                        Apply Coupon
                      </span>
                    )}
                  </div>
                )}
                {!isFCA && (
                  <div className="t-relative">
                    <ConditionalLink
                      to={`/settings/billing/credits?entity=${entityId}`}
                    >
                      <Button
                        size="small"
                        customType="secondary"
                        isLoading={!groupId || isCreditsLoading}
                      >
                        <ConditionalToolTip
                          condition={
                            isLessCredits &&
                            "Your credit balance is below $100. Please add more."
                          }
                        >
                          <span className="t-flex t-items-center">
                            <img
                              src={InkleCreditCoin}
                              alt="InkleCreditCoin"
                              className="t-size-5"
                            />
                            <span className="t-mx-1 t-text-text-60">
                              {formatNumber(total_credits || 0)}
                            </span>
                            <span className="t-text-text-60">Credits</span>
                          </span>
                        </ConditionalToolTip>
                        {currentEntity?.autopay_credits && (
                          <img
                            src={WarningCircle}
                            alt="Warning circle"
                            className="t-h-4 t-m-1 t-pointer-events-none"
                          />
                        )}
                      </Button>
                    </ConditionalLink>
                  </div>
                )}
                <span
                  className={classNames({
                    "t-invisible t-w-0": isPersonalTaxUser,
                  })}
                >
                  <EntitySelector showOnlyUSEntities size="small" />
                </span>
              </div>
            }
          />
        }
      >
        <Product />
        <ApplyCouponModal
          show={openApplyCoupon}
          closeModal={() => setOpenApplyCoupon(false)}
        />
      </DashboardLayout>
      {cart && cart.order_summary.items_count > 0 && (
        <LinkComponent
          to={isCartFetching ? "" : link(`${url}/cart`)}
          onClick={handleOpenCart}
        >
          <div className="t-px-4 t-absolute t-bottom-5 t-left-1/2 t-transform -t-translate-x-1/2 t-flex t-justify-between t-w-4/5 t-p-2 t-pl-4 t-bg-purple-50 t-text-surface t-items-center t-rounded t-text-subtext t-shadow-hover-card-shadow t-z-10 sm:t-left-0 sm:t-right-20 sm:t-w-auto sm:t-translate-x-0 sm:t-gap-2 sm:t-text-body-sm">
            <div className="t-flex t-gap-2 sm:t-items-center sm:t-whitespace-nowrap">
              {pluralize(cart.order_summary.items_count, "item", "items")}{" "}
              <span className="t-text-purple-40">|</span>{" "}
              <AmountSuperScript amount={cart.order_summary.subtotal} />
            </div>
            <Button
              customType="secondary"
              size={isMobile ? "xsmall" : "small"}
              disabled={isCartFetching}
              isLoading={isCartFetching}
            >
              {!isCartFetching && (
                <div className="t-flex t-gap-1.5">
                  <Cart />
                  <span className="sm:t-hidden">View Cart</span>
                  <span className="t-hidden sm:t-inline">View</span>
                </div>
              )}
            </Button>
          </div>
        </LinkComponent>
      )}
    </>
  );
};
